import React, { useState, useCallback, useEffect } from 'react';
import './CommEmailPanel.css';

const API = '/api/opo/communications';

function adminSessionHeaders() {
  try {
    const t = localStorage.getItem('sessionToken');
    return t ? { 'X-Admin-Session': t } : {};
  } catch {
    return {};
  }
}

function commFetch(url, options = {}) {
  const hasBody = options.body != null;
  return fetch(url, {
    cache: 'no-store',
    credentials: 'include',
    ...options,
    headers: {
      ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
      ...adminSessionHeaders(),
      ...(options.headers || {}),
    },
  });
}

/**
 * Gmail agent coding bar (Ollama): natural-language config + whitelisted code edits.
 * Used inline on OPO Communications email panel and as a floating dock on /communications Gmail inbox.
 */
export default function GmailAgentCodingBar({
  variant = 'inline',
  sidebarCollapsed = false,
  onConfigApplied,
  /** Increment with new text to pre-fill the textarea (e.g. from Briefing “Act”). */
  instructionPrefillKey = 0,
  instructionPrefill = '',
  /** When set, “Queue Gmail instruction” sends to the monitor via subagents/:id/instruct. */
  emailActionContext = null,
}) {
  const [codingInput, setCodingInput] = useState('');
  const [codingBusy, setCodingBusy] = useState(false);
  const [queueBusy, setQueueBusy] = useState(false);
  const [codingResult, setCodingResult] = useState(null);
  const [codingExpanded, setCodingExpanded] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [queueMessage, setQueueMessage] = useState(null);

  useEffect(() => {
    if (instructionPrefillKey > 0 && instructionPrefill) {
      setCodingInput(instructionPrefill);
      setCodingExpanded(true);
      setQueueMessage(null);
    }
  }, [instructionPrefillKey, instructionPrefill]);

  const queueGmailInstruction = useCallback(async () => {
    const instr = codingInput.trim();
    const subId = emailActionContext?.subagentId;
    if (!instr || !subId || queueBusy) return;
    setQueueBusy(true);
    setQueueMessage(null);
    setLocalError(null);
    try {
      const r = await commFetch(`${API}/subagents/${encodeURIComponent(subId)}/instruct`, {
        method: 'POST',
        body: JSON.stringify({
          instruction: instr,
          email_ids: emailActionContext.emailIds || [],
          proposal_id: emailActionContext.proposalId ?? null,
        }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || r.statusText);
      setQueueMessage(j.message || 'Instruction queued for gmail-monitor.');
    } catch (e) {
      setLocalError(e.message || 'Queue failed');
    } finally {
      setQueueBusy(false);
    }
  }, [codingInput, emailActionContext, queueBusy]);

  const sendCodingAgent = useCallback(async () => {
    const instr = codingInput.trim();
    if (!instr || codingBusy) return;
    setCodingBusy(true);
    setCodingResult(null);
    setLocalError(null);
    try {
      const r = await commFetch(`${API}/coding-agent`, {
        method: 'POST',
        body: JSON.stringify({ instruction: instr, agent: 'gmail' }),
      });
      const j = await r.json();
      setCodingResult(j);
      if (j.success && j.kind === 'config') {
        if (typeof onConfigApplied === 'function') {
          await onConfigApplied();
        }
      }
    } catch (e) {
      setCodingResult({ success: false, error: e.message });
    } finally {
      setCodingBusy(false);
    }
  }, [codingInput, codingBusy, onConfigApplied]);

  const revertCodingConfig = async (previous) => {
    try {
      const r = await commFetch(`${API}/coding-agent/revert-config`, {
        method: 'POST',
        body: JSON.stringify({ previous }),
      });
      if (r.ok) {
        if (typeof onConfigApplied === 'function') {
          await onConfigApplied();
        }
        setCodingResult(null);
      }
    } catch (e) {
      setLocalError(e.message);
    }
  };

  const applyCodeSnapshot = async (snapshotId) => {
    try {
      const r = await commFetch(`${API}/coding-agent/apply`, {
        method: 'POST',
        body: JSON.stringify({ snapshotId }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'Apply failed');
      setCodingResult((prev) =>
        prev
          ? {
              ...prev,
              applyNote: 'Applied. Restart PM2 gmail-monitor / optiquery if needed.',
              snapshotId,
            }
          : prev
      );
    } catch (e) {
      setLocalError(e.message);
    }
  };

  const rollbackCodeSnapshot = async (snapshotId) => {
    try {
      const r = await commFetch(`${API}/coding-agent/rollback`, {
        method: 'POST',
        body: JSON.stringify({ snapshotId }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'Rollback failed');
      setCodingResult(null);
    } catch (e) {
      setLocalError(e.message);
    }
  };

  const bar = (
    <div className={`comm-email-coding-bar${codingExpanded ? '' : ' comm-email-coding-bar-collapsed'}`}>
      <button type="button" className="comm-email-coding-toggle" onClick={() => setCodingExpanded(!codingExpanded)}>
        {codingExpanded ? '▼' : '▲'} Coding agent (Ollama){' '}
        <span className="comm-email-agent-meta">— change Gmail agent config or propose code edits</span>
      </button>
      {codingExpanded && (
        <>
          <p className="comm-email-hint">
            Prefer natural-language config changes (model, temperature, prompts). Code edits create a snapshot; approve to write
            files (whitelist: gmail-monitor.js, opo-communications.js).
          </p>
          {variant === 'floating' && (
            <p className="comm-email-hint" style={{ marginTop: 4 }}>
              Same agent as <strong>OPO → Communications → Chat</strong> tools; instruct the OptiPrime Gmail agent from your inbox.
            </p>
          )}
          {localError && <div className="comm-email-error">{localError}</div>}
          {emailActionContext?.subagentId && (
            <p className="comm-email-hint" style={{ marginTop: 0 }}>
              Sub-agent context: <strong>{emailActionContext.subagentId}</strong>
              {emailActionContext.proposalId != null ? ` · proposal #${emailActionContext.proposalId}` : ''}. Use{' '}
              <strong>Queue Gmail instruction</strong> to enqueue work for the monitor (not the coding agent).
            </p>
          )}
          {queueMessage && <p className="comm-email-hint" style={{ color: '#34d399' }}>{queueMessage}</p>}
          <div className="comm-email-coding-row">
            <textarea
              className="comm-email-coding-input"
              placeholder="e.g. Change the categorization model to qwen2.5:14b-instruct-q4_K_M"
              value={codingInput}
              onChange={(e) => setCodingInput(e.target.value)}
              disabled={codingBusy || queueBusy}
              rows={2}
            />
            <div className="comm-email-coding-actions-inline">
              <button type="button" className="comm-email-btn" onClick={sendCodingAgent} disabled={codingBusy || queueBusy || !codingInput.trim()}>
                {codingBusy ? 'Running…' : 'Run coding'}
              </button>
              {emailActionContext?.subagentId && (
                <button
                  type="button"
                  className="comm-email-btn comm-email-btn-primary"
                  onClick={queueGmailInstruction}
                  disabled={codingBusy || queueBusy || !codingInput.trim()}
                >
                  {queueBusy ? 'Queuing…' : 'Queue Gmail instruction'}
                </button>
              )}
            </div>
          </div>
          {codingResult && (
            <div className="comm-email-coding-result">
              {codingResult.success === false && <div className="comm-email-error">{codingResult.error || 'Failed'}</div>}
              {codingResult.raw && <pre className="comm-email-pre">{codingResult.raw}</pre>}
              {codingResult.success && codingResult.kind === 'config' && (
                <>
                  <p>{codingResult.explanation}</p>
                  <ul className="comm-email-applied-list">
                    {(codingResult.applied || []).map((a, i) => (
                      <li key={i}>
                        <strong>{a.key}</strong> → {String(a.value).slice(0, 120)}
                        {String(a.value).length > 120 ? '…' : ''}
                      </li>
                    ))}
                  </ul>
                  {codingResult.revertible && codingResult.previous?.length > 0 && (
                    <button type="button" className="comm-email-btn" onClick={() => revertCodingConfig(codingResult.previous)}>
                      Revert config changes
                    </button>
                  )}
                </>
              )}
              {codingResult.success && codingResult.kind === 'code' && (
                <>
                  <p>
                    <strong>{codingResult.target_file}</strong>: {codingResult.explanation}
                  </p>
                  <p className="comm-email-hint">{codingResult.diffPreview}</p>
                  <div className="comm-email-coding-actions">
                    <button
                      type="button"
                      className="comm-email-btn comm-email-btn-danger"
                      onClick={() => applyCodeSnapshot(codingResult.snapshotId)}
                    >
                      Approve &amp; apply file
                    </button>
                    <button type="button" className="comm-email-btn" onClick={() => setCodingResult(null)}>
                      Dismiss
                    </button>
                  </div>
                  {codingResult.snapshotId && (
                    <button
                      type="button"
                      className="comm-email-btn"
                      style={{ marginTop: 8 }}
                      onClick={() => rollbackCodeSnapshot(codingResult.snapshotId)}
                    >
                      Roll back file to snapshot (after apply)
                    </button>
                  )}
                  {codingResult.applyNote && <p className="comm-email-hint">{codingResult.applyNote}</p>}
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );

  if (variant === 'floating') {
    return (
      <div
        className="comms-gmail-coding-dock"
        style={{
          left: sidebarCollapsed ? 'var(--sidebar-collapsed-width)' : 'var(--sidebar-width)',
        }}
      >
        {bar}
      </div>
    );
  }

  return bar;
}
